import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Scan } from "lucide-react";
import { useHoneypot } from "@/hooks/useHoneypot";
import { FAKE_AI_METRICS_RESPONSE, getFakeBotResponse } from "@/lib/fakeBotResponses";
import { getAllMetrics } from "@/lib/metricDefinitions";
import { trackOnboardingEvent, trackStoreClick } from "@/lib/onboardingAnalytics";

interface OnboardingWelcomeProps {
  onContinue: (userInput: string) => void;
  onSkip: () => void;
  onSignIn: () => void;
  tutorialCompleted?: boolean; // If true, hide "Quick tour" and show "Sign up" instead of "Sign in"
  metricsGenerated?: boolean; // If true, user has used the generate metrics feature
}

const EXAMPLE_INPUTS = [
  "Track calories, run distance, fiber, water",
  "track gym time, water, and sleep",
  "Track my smoking habit",
  "Track reading time and meditation",
  "Track coffee intake, screen time, and yoga",
  "Track push-ups, running, and mood",
];

// CYCLING_MESSAGES will be initialized inside component with translations

// WELCOME_MESSAGES moved inside component for translation

// Helper for modulo that handles negative numbers
const mod = (n: number, m: number) => ((n % m) + m) % m;

export const OnboardingWelcome = ({ onContinue, onSkip, onSignIn, tutorialCompleted = false, metricsGenerated = false }: OnboardingWelcomeProps) => {
  const { t } = useTranslation();
  
  // Get cycling messages from translations
  const CYCLING_MESSAGES = (t('onboarding.welcome.cycling_messages', { returnObjects: true }) as string[]).map((text, index) => {
    // First message has special icon handling
    if (index === 0) {
      return { type: 'icon' as const, icon: Scan, iconColor: 'text-green-500', text };
    }
    return { type: 'text' as const, text };
  });
  
  // Get example inputs from translations
  const TRANSLATED_EXAMPLE_INPUTS = t('onboarding.welcome.example_inputs', { returnObjects: true }) as string[];
  
  const WELCOME_MESSAGES = [
    t('welcome.greeting'),
    t('welcome.setup'),
    t('welcome.type_below'),
    t('welcome.what_first')
  ];

  const [input, setInput] = useState("");
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const [welcomeMessageIndex, setWelcomeMessageIndex] = useState(tutorialCompleted ? 2 : 0);
  const [isFocused, setIsFocused] = useState(false);
  const [typedText, setTypedText] = useState("");
  const [isTyping, setIsTyping] = useState(true);
  const [showSetupLaterButton, setShowSetupLaterButton] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [showPostTutorialSetupLater, setShowPostTutorialSetupLater] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const textDisplayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [allMetrics, setAllMetrics] = useState<any[]>([]);
  const [hasSeenAiScan, setHasSeenAiScan] = useState(false);
  const [textScrollOffset, setTextScrollOffset] = useState(0);

  // Detect if Android
  const isAndroid = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);

  // Android keyboard dismiss detection via visualViewport
  // On Android, dismissing the keyboard (e.g. via back button) may not fire blur,
  // so we watch for the viewport expanding back to reset the transform.
  useEffect(() => {
    if (!isAndroid) return;
    const viewport = window.visualViewport;
    if (!viewport) return;

    const fullHeight = viewport.height;

    const handleResize = () => {
      // Keyboard is closed when viewport returns to near full height
      if (viewport.height >= fullHeight * 0.75) {
        setIsFocused(false);
      }
    };

    viewport.addEventListener('resize', handleResize);
    return () => viewport.removeEventListener('resize', handleResize);
  }, [isAndroid]);

  // Honeypot for bot detection
  const { isBot, honeypotProps } = useHoneypot("website_onboarding");

  // Load metrics from database
  useEffect(() => {
    getAllMetrics().then(metrics => {
      setAllMetrics([
        ...metrics,
        { key: "smoking", name: "Smoking", label: "Smoking", emoji: "🚬", unit: "", type: "boolean" },
        { key: "drinks", name: "Drinks", label: "Drinks", emoji: "🍺", unit: "drinks", type: "numeric" },
        { key: "piano", name: "Piano", label: "Piano", emoji: "🎹", unit: "min", type: "numeric" },
        { key: "soccer", name: "Soccer", label: "Soccer", emoji: "⚽", unit: "min", type: "numeric" },
        { key: "custom", name: "Custom", label: "Custom", emoji: "💎", unit: "" }
      ]);
    });
  }, []);

  // Progress color helper
  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return "hsl(142 60% 35%)";
    if (percentage >= 70) return "hsl(142 50% 45%)";
    if (percentage >= 50) return "hsl(60 90% 50%)";
    if (percentage >= 30) return "hsl(30 90% 55%)";
    return "hsl(0 80% 55%)";
  };

  // Show "Set up later" button quickly after page loads
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSetupLaterButton(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Show sign in button shortly after setup later button
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowButtons(true);
    }, 3800);

    return () => clearTimeout(timer);
  }, []);

  // Cycle through welcome messages
  useEffect(() => {
    if (welcomeMessageIndex < WELCOME_MESSAGES.length - 1) {
      // Show "Welcome!" for 2.2s, "Let's get you set up." for 2.1s (1.5s faster), other messages for 4s
      let delay = 4000;
      if (welcomeMessageIndex === 0) delay = 2200;
      else if (welcomeMessageIndex === 1) delay = 2100; // Changed from 3600 to 2100

      const timer = setTimeout(() => {
        setWelcomeMessageIndex(prev => prev + 1);
      }, delay);
      return () => clearTimeout(timer);
    } else if (welcomeMessageIndex === WELCOME_MESSAGES.length - 1) {
      // Wait 3 seconds at the end, then restart from index 1
      const timer = setTimeout(() => {
        setWelcomeMessageIndex(1);
      }, 4500);
      return () => clearTimeout(timer);
    }
  }, [welcomeMessageIndex]);

  // Show "Set up later" button after tutorial completion with delay
  useEffect(() => {
    if (tutorialCompleted) {
      const timer = setTimeout(() => {
        setShowPostTutorialSetupLater(true);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [tutorialCompleted]);

  // Cycle through messages
  useEffect(() => {
    const currentMessage = CYCLING_MESSAGES[messageIndex];
    const duration = currentMessage.text === 'AI scan anything' ? 2500 * 1.5 : 2500;

    const timer = setTimeout(() => {
      setMessageIndex((prev) => (prev + 1) % CYCLING_MESSAGES.length);
    }, duration);

    return () => clearTimeout(timer);
  }, [messageIndex]);

  // Auto-focus disabled for mobile to prevent keyboard popups

  // Typing and backspace animation - continues until user types
  useEffect(() => {
    // Stop animation if user has typed anything
    if (input.length > 0) {
      setTypedText("");
      return;
    }

    const exampleInputs = TRANSLATED_EXAMPLE_INPUTS.length > 0 ? TRANSLATED_EXAMPLE_INPUTS : EXAMPLE_INPUTS;
    const currentExample = exampleInputs[currentExampleIndex % exampleInputs.length];
    let charIndex = 0;
    let typeInterval: NodeJS.Timeout | null = null;
    let deleteTimeout: NodeJS.Timeout | null = null;
    let deleteInterval: NodeJS.Timeout | null = null;
    let cancelled = false;

    // Type forward
    typeInterval = setInterval(() => {
      if (cancelled) return;

      if (charIndex < currentExample.length) {
        setTypedText(currentExample.substring(0, charIndex + 1));
        charIndex++;
      } else {
        // Finished typing, clear interval and start delete phase
        if (typeInterval) clearInterval(typeInterval);

        deleteTimeout = setTimeout(() => {
          if (cancelled) return;

          deleteInterval = setInterval(() => {
            if (cancelled) return;

            if (charIndex > 0) {
              charIndex--;
              setTypedText(currentExample.substring(0, charIndex));
            } else {
              // Finished deleting, move to next example
              if (deleteInterval) clearInterval(deleteInterval);
              if (!cancelled) {
                setCurrentExampleIndex((prev) => (prev + 1) % exampleInputs.length);
              }
            }
          }, 30); // Fast backspace
        }, 1500); // Pause before backspacing
      }
    }, 50); // Typing speed

    return () => {
      cancelled = true;
      if (typeInterval) clearInterval(typeInterval);
      if (deleteTimeout) clearTimeout(deleteTimeout);
      if (deleteInterval) clearInterval(deleteInterval);
    };
  }, [currentExampleIndex, input.length]);

  const handleSubmit = async () => {
    if (input.trim()) {
      // Track analytics
      trackOnboardingEvent({ eventType: "generate_metrics_click" });

      // If bot detected, don't proceed to AI call - skip ahead
      if (isBot) {
        console.warn("[OnboardingWelcome] Bot detected - skipping to tutorial");
        onSkip(); // Skip to tutorial instead of making API call
        return;
      }

      // Automatically prepend "track " if input doesn't start with "track " or "log "
      let processedInput = input.trim();
      const lowerInput = processedInput.toLowerCase();
      if (!lowerInput.startsWith("track ") && !lowerInput.startsWith("log ")) {
        processedInput = "track " + processedInput;
      }

      onContinue(processedInput);
    }
  };

  const handleSignInClick = () => {
    trackOnboardingEvent({ eventType: "sign_in_click" });
    onSignIn();
  };

  const handleSetupLaterClick = () => {
    trackOnboardingEvent({ eventType: "setup_later_click" });
    onSkip();
  };

  return (
    <div
      ref={containerRef}
      className="h-screen bg-background flex flex-col p-6 relative overflow-hidden"
      style={{
        transform: isAndroid && isFocused ? 'translateY(calc(-40vh + 25px))' : 'translateY(0)',
        transition: 'transform 0.3s ease-out'
      }}
      onPointerDown={(e) => {
        // On Android, tapping outside the input should blur it to reset the transform
        if (isAndroid && isFocused && inputRef.current && e.target !== inputRef.current) {
          inputRef.current.blur();
        }
      }}
    >
      {/* Background gradient effect */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 4, ease: "easeInOut" }}
        className="absolute inset-0 pointer-events-none overflow-hidden"
      >
        {/* Dark Gray Outer Glow */}
        <div className="absolute bottom-[-20%] left-1/2 -translate-x-1/2 w-[180%] h-[90%] bg-gray-900/20 blur-[140px] rounded-[100%]" />
        {/* Medium Gray Middle Glow */}
        <div className="absolute bottom-[-30%] left-1/2 -translate-x-1/2 w-[120%] h-[70%] bg-gray-600/20 blur-[110px] rounded-[100%]" />
        {/* Light Gray Center Core */}
        <div className="absolute bottom-[-40%] left-1/2 -translate-x-1/2 w-[80%] h-[60%] bg-gray-400/5 blur-[80px] rounded-[100%]" />

        {/* Noise Overlay - High resolution film grain */}
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 1024 1024' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='1'/%3E%3C/svg%3E")`, backgroundSize: '512px 512px' }} />

        {/* Top fade to background */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-background to-transparent" />
      </motion.div>

      {/* Greeting - Top Left */}
      <div className="mb-8 mt-8 relative z-20" style={{ willChange: 'transform' }}>
        <div className="flex items-center gap-3">
          <motion.img
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            src="/brand-logo-nobg.png"
            alt="GitFit.AI"
            className="w-12 h-12 object-contain"
          />
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-2xl font-bold text-foreground lowercase"
          >
            gitfit<span className="font-extralight opacity-60">.ai</span>
          </motion.h2>
        </div>
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="text-xs text-muted-foreground/40 font-light -mt-1 ml-[60px] lowercase"
        >
          {t('welcome.log_anything')}
        </motion.p>
      </div>

      {/* Center content */}
      <div className="flex-1 flex items-center justify-center relative z-10 -mt-10">
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: [60, 40, 40, 120] }}
          transition={{
            opacity: { duration: 0.5 },
            y: {
              times: [0, 0.3, 0.5, 1],
              duration: 3.1,
              ease: ["easeOut", "easeOut", "easeOut", "easeOut"]
            }
          }}
          className="w-full max-w-md space-y-6"
          style={{ isolation: 'isolate' }}
        >

          {/* Call to action */}
          <motion.div
            className="text-center space-y-1 relative"
          >
            {/* Background dashboard image with elegant reveal */}
            <div
              className="absolute left-1/2 -translate-x-1/2 -top-[300px] max-h-[700px]:-top-[280px] w-[380px] h-[550px] pointer-events-none -z-10"
              style={{
                maskImage: "linear-gradient(to top, transparent 0%, transparent 25%, black 80%)",
                WebkitMaskImage: "linear-gradient(to top, transparent 0%, transparent 25%, black 80%)"
              }}
            >
              <motion.img
                initial={{ opacity: 0, y: 80 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: 1.8,
                  duration: 3.5,
                  ease: [0.19, 1, 0.22, 1]
                }}
                src="/iphone-screenshots/dashboard-aaron.png"
                alt=""
                className="w-full h-full object-contain"
                style={{ opacity: 0.6 }}
              />
            </div>

            <div className="h-8 relative z-10 flex items-center justify-center w-full mb-2">
              <AnimatePresence mode="wait">
                <motion.h3
                  key={welcomeMessageIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5 }}
                  className="text-lg font-semibold text-foreground absolute"
                >
                  {WELCOME_MESSAGES[welcomeMessageIndex]}
                </motion.h3>
              </AnimatePresence>
            </div>
            <AnimatePresence>
              {welcomeMessageIndex >= 1 && (
                <motion.p
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ delay: 1.9, duration: 0.5, ease: "easeOut" }}
                  className="text-sm text-muted-foreground font-light relative z-10"
                >
                  {t('welcome.habit_prompt')}
                </motion.p>
              )}
            </AnimatePresence>

            {/* Scrolling Metrics - moved here */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.8, duration: 0.8, ease: "easeOut" }}
              className="w-full max-w-md mx-auto opacity-60 mt-4"
            >
              <div
                className="relative w-full overflow-hidden h-8"
                style={{
                  maskImage: "linear-gradient(to right, transparent, transparent 15%, black 35%, black 65%, transparent 85%, transparent)",
                  WebkitMaskImage: "linear-gradient(to right, transparent, transparent 15%, black 35%, black 65%, transparent 85%, transparent)"
                }}
              >
                <motion.div
                  className="flex gap-8 absolute"
                  animate={{
                    x: [0, -1200],
                  }}
                  transition={{
                    x: {
                      repeat: Infinity,
                      repeatType: "loop",
                      duration: 40,
                      ease: "linear",
                    },
                  }}
                >
                  {[...allMetrics, ...allMetrics].filter(metric => {
                    // Filter out metrics with unknown or problematic emojis
                    const emoji = metric.emoji;
                    if (!emoji || emoji === '❓' || emoji === '⚠️' || emoji === '�') return false;
                    // Additional check for emojis that don't render well
                    const problemEmojis = ['🫘', '🧈', '🧋', '🫗', '🫖', '🥤'];
                    return !problemEmojis.includes(emoji);
                  }).map((metric, idx) => {
                    const isBoolean = metric.type === "boolean";
                    const seed = idx % allMetrics.length;
                    const randomValue = isBoolean
                      ? (seed % 2 === 0 ? 100 : 0)
                      : 40 + (seed * 7) % 60;

                    const radius = 12;
                    const circumference = 2 * Math.PI * radius;
                    const strokeDashoffset = circumference - (randomValue / 100) * circumference;

                    return (
                      <div key={idx} className="flex-shrink-0">
                        <svg className="w-8 h-8" viewBox="0 0 32 32">
                          <circle
                            cx="16"
                            cy="16"
                            r={radius}
                            stroke="hsl(var(--muted))"
                            strokeWidth="1.5"
                            fill="none"
                            transform="rotate(-90 16 16)"
                          />
                          <circle
                            cx="16"
                            cy="16"
                            r={radius}
                            stroke={getProgressColor(randomValue)}
                            strokeWidth="1.5"
                            fill="none"
                            strokeDasharray={circumference}
                            strokeDashoffset={strokeDashoffset}
                            strokeLinecap="round"
                            transform="rotate(-90 16 16)"
                            style={!isBoolean ? {
                              transition: "stroke-dashoffset 0.5s cubic-bezier(0.4, 0, 0.2, 1), stroke 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
                            } : {}}
                          />
                          <text x="16" y="20" textAnchor="middle" fontSize="14" fill="currentColor">
                            {metric.emoji}
                          </text>
                        </svg>
                      </div>
                    );
                  })}
                </motion.div>
              </div>
            </motion.div>
          </motion.div>

          {/* AI Input Section */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              opacity: { delay: 0.8, duration: 0.3 },
              y: { delay: 1.6, duration: 0.4 }
            }}
            className="space-y-4"
          >
            {/* Honeypot field - hidden from users but visible to bots */}
            <input {...honeypotProps} />

            {/* Input field */}
            <div className="space-y-2">
              <style>{`
                  @property --angle {
                    syntax: '<angle>';
                    initial-value: 0deg;
                    inherits: false;
                  }

                  @keyframes rotate-border {
                    from {
                      --angle: 0deg;
                    }
                    to {
                      --angle: 360deg;
                    }
                  }

                  @keyframes rainbow-move {
                    0% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                  }

                  @keyframes glow-appear {
                    0% { opacity: 0; }
                    50% { opacity: 0.15; }
                    100% { opacity: 0; }
                  }

                  .input-wisp-wrapper::after {
                    content: '';
                    position: absolute;
                    inset: -2px;
                    border-radius: 9999px;
                    padding: 2px;
                    background: conic-gradient(
                      from var(--angle),
                      transparent 0deg,
                      transparent 300deg,
                      rgba(34, 197, 94, 0.4) 330deg,
                      rgba(34, 197, 94, 0.8) 360deg
                    );
                    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
                    -webkit-mask-composite: xor;
                    mask-composite: exclude;
                    animation: rotate-border 3s linear infinite;
                    pointer-events: none;
                    transition: opacity 0.3s ease;
                  }

                  .input-wisp-wrapper.focused::after {
                    opacity: 0;
                  }

                  .rainbow-outerglow {
                    position: absolute;
                    inset: -10px;
                    border-radius: 9999px;
                    background: linear-gradient(
                      45deg,
                      #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #9400d3,
                      #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #9400d3
                    );
                    background-size: 400% 400%;
                    filter: blur(16px);
                    z-index: -1;
                    opacity: 0;
                    animation: 
                      rainbow-move 15s ease infinite,
                      glow-appear 4s ease-in-out 3s forwards;
                  }
                `}</style>
              <div className={`relative input-wisp-wrapper ${isFocused ? 'focused' : ''}`}>
                <div className="rainbow-outerglow" />
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder=""
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    // Calculate scroll offset
                    setTimeout(() => {
                      if (inputRef.current && textDisplayRef.current) {
                        const containerWidth = inputRef.current.offsetWidth - 24; // minus padding
                        const textWidth = textDisplayRef.current.offsetWidth;
                        const offset = Math.max(0, textWidth - containerWidth);
                        setTextScrollOffset(offset);
                        inputRef.current.scrollLeft = inputRef.current.scrollWidth;
                        inputRef.current.selectionStart = inputRef.current.value.length;
                        inputRef.current.selectionEnd = inputRef.current.value.length;
                      }
                    }, 0);
                  }}
                  onFocus={() => {
                    setIsFocused(true);
                    // Scroll to end on focus
                    setTimeout(() => {
                      if (inputRef.current) {
                        inputRef.current.scrollLeft = inputRef.current.scrollWidth;
                        inputRef.current.selectionStart = inputRef.current.value.length;
                        inputRef.current.selectionEnd = inputRef.current.value.length;
                      }
                    }, 0);
                  }}
                  onBlur={() => setIsFocused(false)}
                  onClick={() => {
                    // Scroll to end on click
                    setTimeout(() => {
                      if (inputRef.current) {
                        inputRef.current.scrollLeft = inputRef.current.scrollWidth;
                        inputRef.current.selectionStart = inputRef.current.value.length;
                        inputRef.current.selectionEnd = inputRef.current.value.length;
                      }
                    }, 0);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSubmit();
                    }
                  }}
                  className="text-base h-12 rounded-full caret-transparent text-transparent bg-black/70 backdrop-blur-md border-white/10 shadow-[0_0_20px_rgba(100,255,218,0.3),0_0_40px_rgba(100,255,218,0.15),inset_0_0_12px_rgba(100,255,218,0.1)]"
                  maxLength={100}
                />
                {/* Custom blinking caret and placeholder animation */}
                <div className="absolute inset-0 pointer-events-none px-3 overflow-hidden">
                  <div className="h-full flex items-center">
                    <div
                      ref={textDisplayRef}
                      className="text-base flex items-center whitespace-nowrap"
                      style={{ transform: `translateX(-${textScrollOffset}px)`, transition: 'transform 0.1s ease-out' }}
                    >
                      {!input && (
                        <>
                          <motion.span
                            animate={{ opacity: [1, 0, 1] }}
                            transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                            className="mr-0.5 text-foreground"
                          >
                            |
                          </motion.span>
                          <span className="text-sm text-muted-foreground">{typedText}</span>
                        </>
                      )}
                      {input && (
                        <>
                          <span className="text-foreground">{input}</span>
                          <motion.span
                            animate={{ opacity: [1, 0, 1] }}
                            transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                            className="ml-0.5 text-foreground"
                          >
                            |
                          </motion.span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Continue button - only show when there's input */}
            <AnimatePresence>
              {input.trim() && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <Button
                    onClick={handleSubmit}
                    className="w-full h-12 text-sm font-semibold rounded-full shadow-[0_0_30px_-5px_hsl(var(--primary)/0.6)] hover:shadow-[0_0_30px_-5px_hsl(var(--primary)/0.8)] transition-all duration-300 hover:scale-[1.02] bg-gradient-to-r from-primary to-primary/80"
                    size="lg"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t('welcome.start_tracking')}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Skip and Sign-in options - Reserve space to prevent layout shift */}
            <div className="min-h-20 flex items-center justify-center">
              <div className="flex flex-col items-center justify-center gap-3 text-xs w-full">
                {/* Quick tour / Set up later button */}
                <div className="h-7 flex items-center justify-center">
                  <AnimatePresence mode="wait">
                    {!tutorialCompleted && showSetupLaterButton && (
                      <motion.div
                        key="quick-tour"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      >
                        <Button variant="ghost" size="sm" onClick={handleSetupLaterClick} className="font-extralight text-xs h-auto py-1 px-2 text-muted-foreground/40">
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4, duration: 0.3 }}
                          >
                            {t('welcome.or')}{" "}
                          </motion.span>
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.7, duration: 0.3 }}
                          >
                            {t('welcome.quick_tour')}
                          </motion.span>
                        </Button>
                      </motion.div>
                    )}
                    {tutorialCompleted && showPostTutorialSetupLater && (
                      <motion.div
                        key="setup-later"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      >
                        <Button variant="ghost" size="sm" onClick={handleSetupLaterClick} className="font-extralight text-xs h-auto py-1 px-2 text-muted-foreground/40">
                          {t('welcome.setup_later')}
                        </Button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Already have account button */}
                <div className="h-7 flex items-center justify-center -mt-3.5">
                  <AnimatePresence mode="wait">
                    {showButtons && !tutorialCompleted && (
                      <motion.div
                        key="sign-in"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                      >
                        <Button variant="ghost" size="sm" onClick={handleSignInClick} className="font-extralight text-[10px] h-auto py-1 px-2 text-muted-foreground/30">
                          {t('welcome.sign_up_now')}
                        </Button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* App Store / Play Store download buttons (mobile web only) */}
                {showButtons && !tutorialCompleted && (
                  <div className="flex flex-row items-center justify-center gap-3 -mt-2">
                    <motion.a
                      href="https://apps.apple.com/us/app/gitfit-ai/id6754495386"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackStoreClick("apple", "onboarding")}
                      initial={{ opacity: 0, y: 12, scale: 0.92 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: 0.5, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                      className="cursor-pointer rounded-xl transition-all duration-200 ease-out hover:scale-105 active:scale-95"
                    >
                      <img
                        src="/Download_AppStore.png"
                        alt={t('showcase.download_app_store')}
                        className="h-9 w-auto opacity-60"
                      />
                    </motion.a>
                    <motion.a
                      href="https://play.google.com/store/apps/details?id=ai.gitfit.app&pcampaignid=web_share"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackStoreClick("google", "onboarding")}
                      initial={{ opacity: 0, y: 12, scale: 0.92 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: 0.7, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                      className="cursor-pointer rounded-xl transition-all duration-200 ease-out hover:scale-105 active:scale-95"
                    >
                      <img
                        src="/Download_PlayStore.png"
                        alt={t('showcase.download_play_store')}
                        className="h-9 w-auto opacity-60"
                      />
                    </motion.a>
                  </div>
                )}
              </div>
            </div>

            {/* Cycling Messages */}
            <div className="h-8 flex items-center justify-center mt-2">
              <AnimatePresence mode="wait">
                {(() => {
                  const currentMessage = CYCLING_MESSAGES[messageIndex];
                  const Icon = currentMessage.icon;
                  const isAiScanMessage = currentMessage.text === 'AI scan anything';
                  const shouldAnimateScale = isAiScanMessage && !hasSeenAiScan;

                  // Mark as seen when AI scan message appears
                  if (isAiScanMessage && !hasSeenAiScan) {
                    setTimeout(() => setHasSeenAiScan(true), 0);
                  }

                  return (
                    <motion.div
                      key={`message-${messageIndex}`}
                      initial={{
                        opacity: shouldAnimateScale ? 1 : 0,
                        y: shouldAnimateScale ? 0 : 10,
                        scale: shouldAnimateScale ? 0 : 1
                      }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 1 }}
                      transition={{
                        opacity: { duration: shouldAnimateScale ? 0 : 0.3 },
                        y: { duration: 0.3 },
                        scale: {
                          duration: shouldAnimateScale ? 0.6 : 0.3,
                          ease: shouldAnimateScale ? [0.16, 1, 0.3, 1] : "easeOut",
                          delay: shouldAnimateScale ? 0.2 : 0
                        }
                      }}
                      className="flex items-center gap-2 text-xs font-medium text-muted-foreground origin-center"
                    >
                      {currentMessage.type === 'icon' && Icon && (
                        <Icon className={`w-3.5 h-3.5 ${currentMessage.iconColor}`} />
                      )}
                      <span>{currentMessage.text}</span>
                    </motion.div>
                  );
                })()}
              </AnimatePresence>
            </div>

            {/* Product Hunt Badge */}
            <div className="flex flex-col items-center gap-1 mt-auto pb-2">
              <span className="text-[10px] font-light tracking-widest uppercase text-muted-foreground/40">{t('showcase.featured_on_product_hunt')}</span>
              <a href="https://www.producthunt.com/products/gitfit-ai?embed=true&utm_source=badge-top-post-topic-badge&utm_medium=badge&utm_campaign=badge-gitfit-ai" target="_blank" rel="noopener noreferrer" className="opacity-50">
                <img alt={t('showcase.product_hunt_badge_alt')} width="250" height="54" src="https://api.producthunt.com/widgets/embed-image/v1/top-post-topic-badge.svg?post_id=1098929&theme=dark&period=weekly&topic_id=43&t=1774555615232" />
              </a>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};
